import React, { useState, useMemo } from 'react';
import TeslaButton from '../common/TeslaButton';
import ProfileAvatar from '../common/ProfileAvatar';
import { resolveWebImageSrc } from '../../utils/resolveMediaUrl';
import { useAppSelector } from '../../hooks/useRedux';
import { formatDistanceToNow } from 'date-fns';
import reviewService, { Review, CreateReviewRequest } from '../../services/reviewService';
import { useUI } from '../../context/UIContext';
import { reviewAuthorName, reviewAuthorParty } from '../../utils/appointmentReviewDirection';

interface ReviewFormProps {
  stylistId: string;
  stylistName: string;
  stylistPhoto?: string;
  appointmentId?: string; // Optional appointment ID for review
  existingReview?: Review | null;
  onSuccess?: () => void;
  onCancel?: () => void;
  isDraft?: boolean;
}

// Review templates for CLIENT reviewing STYLIST (stylist-review)
const STYLIST_REVIEW_TEMPLATES: { [key: number]: string[] } = {
  5: [
    "Fantastic service! Will definitely recommend to friends.",
    "Love it! Will definitely come back for more services.",
    "She is so lovely and really knows her work. Amazing results!",
    "Excellent service, highly professional and skilled.",
    "Amazing experience, exceeded all my expectations!",
    "Best stylist I've ever had! Perfect attention to detail.",
    "Perfect service, couldn't be happier with the results."
  ],
  4: [
    "Great service, very satisfied with the work done.",
    "Good experience overall, professional and friendly.",
    "Professional service, happy with the results.",
    "Would recommend to others, good quality work.",
    "Pleasant experience, will return for future services.",
    "Nice work, happy with how everything turned out."
  ],
  3: [
    "Decent service, but there's room for improvement.",
    "Average experience, met my basic expectations.",
    "Okay service, nothing exceptional but acceptable.",
    "Satisfactory work, but could have been better.",
    "Fair service, some improvements needed in technique."
  ],
  2: [
    "Below my expectations, service needs improvement.",
    "Not fully satisfied with the service provided.",
    "Could be much better, disappointed with the results.",
    "Disappointing experience, expected more professionalism.",
    "Service quality needs significant work."
  ],
  1: [
    "Very poor experience, would not recommend.",
    "Extremely disappointed with the service quality.",
    "Service was unsatisfactory and unprofessional.",
    "Would not recommend to anyone.",
    "Needs significant improvement in skills and service."
  ]
};

const ReviewForm: React.FC<ReviewFormProps> = ({
  stylistId,
  stylistName,
  stylistPhoto,
  appointmentId,
  existingReview = null,
  onSuccess,
  onCancel,
  isDraft = false
}) => {
  const { showSnackbar } = useUI();
  const { user } = useAppSelector(state => state.auth);
  
  // Component state
  const [rating, setRating] = useState<number>(
    existingReview?.rating?.overall || 0
  );
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [comment, setComment] = useState(
    existingReview?.review?.text || ''
  );
  // Reviews cannot be edited - if review exists, show it in read-only mode
  const hasExistingReview = !!existingReview;
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get templates based on current rating
  const templates = useMemo(() => {
    return rating > 0 ? (STYLIST_REVIEW_TEMPLATES[rating] || []) : [];
  }, [rating]);

  const handleTemplateSelect = (template: string) => {
    setComment(template);
  };

  const handleRatingClick = (value: number) => {
    setRating(value);
  };

  const handleRatingHover = (value: number) => {
    setHoverRating(value);
  };

  const handleRatingLeave = () => {
    setHoverRating(0);
  };

  // Handle review submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (rating === 0) {
      setError('Please select a rating');
      return;
    }

    if (!comment.trim()) {
      setError('Please provide a review comment');
      return;
    }

    // If review already exists, don't allow submission
    if (hasExistingReview) {
      setError('You have already submitted a review for this appointment.');
      return;
    }

    // Create new review - need appointmentId
    if (!appointmentId) {
      setError('Appointment ID is required to create a review');
      return;
    }

    setIsSubmitting(true);
    try {
      const reviewData: CreateReviewRequest = {
        appointmentId,
        reviewType: 'stylist-review',
        rating: {
          overall: rating,
          aspects: {} // No aspect ratings
        },
        review: comment,
        pros: [],
        cons: []
      };

      await reviewService.createReview(reviewData);
      
      setSuccessMessage('Review submitted successfully!');
      showSnackbar('Review submitted successfully!', 'success');
      
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || '';
      
      // Check for specific error messages and provide friendly feedback
      if (errorMessage.includes('Review already exists') || errorMessage.includes('already been submitted')) {
        const friendlyMessage = 'You have already submitted a review for this appointment.';
        setError(friendlyMessage);
        showSnackbar(friendlyMessage, 'error');
      } else if (err.response?.status === 400) {
        const friendlyMessage = errorMessage || 'Unable to submit review. Please try again.';
        setError(friendlyMessage);
        showSnackbar(friendlyMessage, 'error');
      } else {
        const friendlyMessage = 'Failed to submit review. Please check your connection and try again.';
        setError(friendlyMessage);
        showSnackbar(friendlyMessage, 'error');
      }
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Handle cancel button click
  const handleCancelClick = () => {
    // Clear form
    setRating(0);
    setComment('');
    
    if (onCancel) {
      onCancel();
    }
  };
  
  // Format timestamp
  const formatTime = (timestamp: string) => {
    try {
      const date = new Date(timestamp);
      return formatDistanceToNow(date, { addSuffix: true });
    } catch (error) {
      return timestamp;
    }
  };

  // Get rating text
  const getRatingText = (ratingValue: number) => {
    switch (ratingValue) {
      case 5: return 'Excellent';
      case 4: return 'Good';
      case 3: return 'Average';
      case 2: return 'Poor';
      case 1: return 'Very Poor';
      default: return '';
    }
  };

  // View mode - showing the existing review (read-only)
  if (hasExistingReview) {
    const author = reviewAuthorParty(existingReview);
    const authorId = author?._id || author?.id;
    const viewerId = user?.id || (user as { _id?: string } | null)?._id;
    const viewerIsAuthor = Boolean(authorId && viewerId && String(authorId) === String(viewerId));
    const reviewerName = reviewAuthorName(existingReview, user);
    const reviewerPhoto = viewerIsAuthor && resolveWebImageSrc(user?.profilePhoto)
      ? user?.profilePhoto
      : author?.profilePhoto;

    return (
      <div className="review-card">
        <div className="review-card__header">
          <div className="review-card__user">
            <ProfileAvatar
              className="review-card__avatar"
              name={reviewerName}
              photo={reviewerPhoto}
            />
            <div className="review-card__user-info">
              <div className="review-card__username">
                {reviewerName}
              </div>
              <div className="review-card__date">
                {existingReview && formatTime(existingReview.createdAt)}
              </div>
            </div>
          </div>
        </div>
        
        <div className="review-card__rating">
          {[1, 2, 3, 4, 5].map((value) => (
            <span 
              key={value}
              className={`review-card__star ${value <= (existingReview?.rating?.overall || 0) ? 'active' : ''}`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
            </span>
          ))}
        </div>
        
        <p className="review-card__comment">{existingReview?.review?.text}</p>
        
        {successMessage && (
          <div className="message message--success">
            {successMessage}
          </div>
        )}
      </div>
    );
  }

  // Form for creating a new review
  return (
    <form onSubmit={handleSubmit} className="review-form">
      <div className="review-form__header">
        <ProfileAvatar
          className="review-form__avatar"
          name={stylistName}
          photo={stylistPhoto}
        />
        <h3 className="review-form__title">
          Review for {stylistName}
        </h3>
      </div>
      
      {error && (
        <div className="message message--error">
          {error}
        </div>
      )}
      
      {successMessage && (
        <div className="message message--success">
          {successMessage}
        </div>
      )}

      <div className="review-form__section">
        <label className="review-form__label">Overall Rating</label>
        <div 
          className="review-form__stars" 
          onMouseLeave={handleRatingLeave}
        >
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              className={`review-form__star ${
                value <= (hoverRating || rating) ? 'active' : ''
              }`}
              onClick={() => handleRatingClick(value)}
              onMouseEnter={() => handleRatingHover(value)}
              aria-label={`Rate ${value} out of 5`}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/>
              </svg>
            </button>
          ))}
        </div>
        {rating > 0 && (
          <div className="review-form__rating-text">
            {getRatingText(rating)}
          </div>
        )}
      </div>

      {templates.length > 0 && (
        <div className="review-form__section">
          <label className="review-form__label">Quick Templates</label>
          <p className="review-form__template-subtitle">
            Click a template to use it, or write your own
          </p>
          <div className="review-form__templates">
            {templates.map((template, index) => (
              <button
                key={index}
                type="button"
                className="review-form__template-button"
                onClick={() => handleTemplateSelect(template)}
              >
                {template}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="review-form__section">
        <label htmlFor="review-comment" className="review-form__label">Your Review</label>
        <textarea
          id="review-comment"
          className="review-form__textarea"
          rows={4}
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Share your experience with this stylist..."
          maxLength={500}
          required
        />
        <div className="review-form__character-count">
          {comment.length}/500
        </div>
      </div>

      <div className="review-form__actions">
        <TeslaButton
          type="button"
          variant="secondary"
          onClick={handleCancelClick}
          disabled={isSubmitting}
        >
          Cancel
        </TeslaButton>
        
        <TeslaButton
          type="submit"
          variant="primary"
          loading={isSubmitting}
          disabled={isSubmitting || rating === 0 || !comment.trim()}
        >
          {existingReview ? 'Update' : 'Submit'} Review
        </TeslaButton>
      </div>
    </form>
  );
};

export default ReviewForm;
